import { z } from "zod";
import { storefrontAnalyticsSchema } from "./analytics.js";
import { storefrontRealtimeSchema } from "./battles.js";
import { boxCategorySchema } from "./boxCategories.js";
import { cookieBannerThemeSchema } from "./cookieBannerTheme.js";
import { currencySchema } from "./currency.js";
import { forgeBoxInfoSchema, storefrontForgeSchema } from "./forge.js";
import { boxOpenerSchema, openerThemeOverridesSchema } from "./openerStyles.js";
import { CENTS, clientSeedSchema, dropTableItemSchema, seedRevealSchema } from "./primitives.js";
import { STOREFRONT_RARITY_KEYS } from "./rarityTiers.js";
import { storefrontShippingSchema } from "./shippingDestinations.js";
import { socialLinksSchema } from "./socialLinks.js";
import { storefrontSoundsSchema } from "./sounds.js";
import { publicStorefrontLegalSchema } from "./storefrontLegal.js";
import { pageTemplateChoicesSchema, storefrontTemplateIdSchema } from "./storefrontTemplates.js";
import { storefrontTerminologySchema } from "./storefrontTerminology.js";
import {
  STOREFRONT_TOKEN_DEFAULTS,
  STOREFRONT_TOKEN_KEYS,
  type StorefrontTokenKey,
} from "./storefrontTokens.js";
import { refuseUnacceptedThemeColors } from "./themeColorInput.js";
import { themeSpecShape } from "./themeSpec.js";

export * from "./analytics.js";
export * from "./battles.js";
export * from "./boxCategories.js";
export * from "./boxEvents.js";
export * from "./boxListFilters.js";
export * from "./catalogItem.js";
export * from "./chat.js";
export * from "./color.js";
export * from "./cookieBannerTheme.js";
export * from "./countries.js";
export * from "./currency.js";
export * from "./dropTable.js";
export * from "./dropTableDisclosure.js";
export * from "./fairnessReadout.js";
export * from "./forge.js";
export * from "./forgeBuilder.js";
export * from "./giftCodes.js";
export * from "./inventoryUnits.js";
export * from "./itemImages.js";
export * from "./notifications.js";
export * from "./openerControls.js";
export * from "./openerStyles.js";
export * from "./openerTheme.js";
export * from "./primitives.js";
export * from "./profile.js";
export * from "./rarityColor.js";
export * from "./rarityTiers.js";
export * from "./rbac.js";
export * from "./revealHold.js";
export * from "./sampleOpenerBox.js";
export * from "./shippingAddress.js";
export * from "./shippingDestinations.js";
export * from "./shopifyOpenerTheme.js";
export * from "./shopifyRuleBox.js";
export * from "./socialLinks.js";
export * from "./sounds.js";
export * from "./storefrontBanners.js";
export * from "./storefrontCopy/account.js";
export * from "./storefrontCopy/battles.js";
export * from "./storefrontCopy/box.js";
export * from "./storefrontCopy/boxes.js";
export * from "./storefrontCopy/fairness.js";
export * from "./storefrontCopy/forge.js";
export * from "./storefrontCopy/frame.js";
export * from "./storefrontCopy/home.js";
export * from "./storefrontCopy/inventory.js";
export * from "./storefrontCopy/login.js";
export * from "./storefrontCopy/shared.js";
export * from "./storefrontFonts.js";
export * from "./storefrontLegal.js";
export * from "./storefrontPalette.js";
export * from "./storefrontSections.js";
export * from "./storefrontSignIn.js";
export * from "./storefrontTemplates.js";
export * from "./storefrontTerminology.js";
export * from "./storefrontThemeVars.js";
export * from "./storefrontTokens.js";
export * from "./svgIcon.js";
export * from "./tenantAssets.js";
export * from "./tenantModules.js";
export * from "./themeColorInput.js";
export * from "./themeSpec.js";
export * from "./username.js";
export * from "./usStates.js";
export * from "./volatility.js";

// ---- shared primitives -------------------------------------------------------
// CENTS, clientSeedSchema, dropTableItemSchema and seedRevealSchema live in ./primitives.ts,
// re-exported above, so ./battles.ts can use them without importing this barrel back.

export const modeSchema = z.enum(["live", "test"]);
export type Mode = z.infer<typeof modeSchema>;

export const errorCodeSchema = z.enum([
  "unauthorized",
  "forbidden",
  "session_expired",
  "not_found",
  "insufficient_balance",
  "box_version_changed",
  "box_not_active",
  "box_not_openable",
  "invalid_quantity",
  "invalid_client_seed",
  "reveal_pending",
  "purchase_price_mismatch",
  "invalid_drop_table",
  "duplicate",
  "opens_paused",
  "account_locked",
  "fraud_declined",
  "withdrawal_limit_exceeded",
  "kyc_required",
  "rate_limited",
  "payments_unavailable",
  "invalid_request",
  "tenant_archived",
  "battles_disabled",
  "battle_not_joinable",
  "battle_seat_taken",
  "battle_already_joined",
  "battle_not_creator",
  "invalid_battle_settings",
  "battle_price_mismatch",
  "battle_not_completed",
  "battle_not_stale",
  "realtime_unavailable",
  "invalid_shipping_address",
  "shipping_unavailable",
  "shipping_address_required",
  "unsupported_shipping_destination",
  "shipping_fee_mismatch",
  "address_unverifiable",
  "address_country_mismatch",
  "shipping_config_conflict",
  "username_taken",
  "reveal_already_sold",
  "reveal_already_claimed",
  "inventory_item_not_withdrawable",
  "inventory_item_not_sellable",
  "chat_disabled",
  "chat_muted",
  "chat_message_rejected",
  "chat_message_not_found",
  "chat_room_not_found",
  "chat_report_not_open",
  "username_required",
  "forge_disabled",
  "forge_drop_table_invalid",
  "forge_cut_invalid",
  "forge_daily_limit",
  "forge_nothing_to_claim",
  "forge_name_rejected",
  "forge_image_invalid",
  "order_not_cancellable",
  "fulfillment_unavailable",
  "shopify_shop_not_linked",
  "shopify_shop_ineligible",
  "shopify_box_ineligible",
  "shopify_billing_required",
  "shopify_reveal_token_invalid",
  "box_purchase_not_revealable",
  "store_credit_short",
  "store_credit_unavailable",
  "gift_code_not_redeemable",
  "internal",
]);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

/**
 * One member per refusal cause of `inventory_item_not_withdrawable`; carried in `details.reason`
 * beside the `inventoryItemId` it names. `sold` is a unit already sold back to the house, `withdrawn`
 * one an order has already shipped,
 * `refunded` one a cancelled order already paid back as a voucher, and `voucher` the voucher
 * itself, which is worth money rather than being a thing to post.
 *
 * The last four are about the market rather than the unit, so they can clear on their own:
 * `listing_unavailable` is a vendor listing that went INACTIVE or has no unit left,
 * `price_drift` a live vendor price above the one the entry stored, `catalog_out_of_stock` a
 * FIXED catalog item whose stock is all promised, and `catalog_unfulfillable` a DYNAMIC catalog
 * item that is archived or cannot be sourced, or a catalog item nobody can find. A FIXED item's
 * status never refuses it: stock alone decides. A read that tells a shopper beforehand which of
 * their items would be refused reports the same members.
 */
export const INVENTORY_WITHDRAWAL_ISSUES = [
  "sold",
  "withdrawn",
  "refunded",
  "voucher",
  "listing_unavailable",
  "price_drift",
  "catalog_out_of_stock",
  "catalog_unfulfillable",
] as const;
export type InventoryWithdrawalIssue = (typeof INVENTORY_WITHDRAWAL_ISSUES)[number];

/**
 * One member per refusal cause of `inventory_item_not_sellable`; carried in `details.reason`
 * beside the `inventoryItemId` it names. `sold` is a unit already sold back, `withdrawing` one an
 * order is shipping, `withdrawn` one an order has shipped, `no_value` one worth nothing, which is
 * withdraw-only, and `refunded` one a cancelled order already paid back as a voucher, which is the
 * unit to sell instead.
 */
export const INVENTORY_SALE_ISSUES = [
  "sold",
  "withdrawing",
  "withdrawn",
  "no_value",
  "refunded",
] as const;
export type InventorySaleIssue = (typeof INVENTORY_SALE_ISSUES)[number];

export const errorResponseSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;

export const MAX_OPEN_AMOUNT = 36;

// ---- boxes ---------------------------------------------------------------------

/**
 * A box's art beyond `imageUrl`: a background behind it and an overlay above it, and a mobile set
 * of all three. "" = none; an empty mobile slot falls back to the desktop one.
 */
export const boxArtSchema = z.object({
  background: z.string(),
  overlay: z.string(),
  mobile: z.object({ image: z.string(), background: z.string(), overlay: z.string() }),
});
export type BoxArt = z.infer<typeof boxArtSchema>;

/** A box's most valuable item that can drop, as a browse tile shows it. */
export const boxTopItemSchema = z.object({
  name: z.string(),
  imageUrl: z.string(),
  value: z.int().nonnegative().describe(CENTS),
});
export type BoxTopItem = z.infer<typeof boxTopItemSchema>;

export const boxSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  imageUrl: z.string(),
  priceCents: z.number().describe(CENTS),
  version: z.number(),
  /** True when the box is a box template (`tenant_id` NULL), shared by the platform. */
  isCatalog: z.boolean(),
  /** Merchandising: the storefront home page's featured row shows these and nothing else. */
  featured: z.boolean(),
  openedCount: z.number(),
  /** Optional so a client built against an older server still parses its boxes. */
  art: boxArtSchema.optional(),
  /** When the box was added; the storefront's "New" tab reads it. Optional like `art`. */
  createdAt: z.string().optional().describe("When the box was added."),
  /**
   * The most valuable entry of the current version that can drop (weight above 0), for a browse
   * tile. Only the lists (`GET /v1/boxes`, `GET /v1/forge/boxes`) carry it; absent when no entry
   * can drop, and on an older server.
   */
  topItem: boxTopItemSchema
    .optional()
    .describe(
      "The most valuable item of the current version that can drop (weight above 0). Sent on GET /v1/boxes and GET /v1/forge/boxes only; absent when no entry can drop.",
    ),
  /**
   * The box categories the box is in, in list order: every game its current version's catalog
   * items name, and each game's family (`BOX_CATEGORIES`). Read from the items, never assigned.
   * Sent on GET /v1/boxes and GET /v1/boxes/{boxId}; `[]` when no item names a game, absent on an
   * older server.
   */
  categories: z
    .array(boxCategorySchema)
    .optional()
    .describe(
      "The box categories the box is in, in list order: every game its current version's catalog items name (POKEMON, BASEBALL), and each game's family (SPORTS, OTHER). Read from the items, never assigned. Sent on GET /v1/boxes and GET /v1/boxes/{boxId}; [] when no item names a game.",
    ),
  /**
   * How swingy the current version is (`calculateVolatility`): one over the chance of rolling an
   * item worth at least the box's EV, read on a 0–16 scale in four levels (`readVolatility`). The
   * raw number, unclamped: a one-in-10,000 chase sends 10,000, and anything above 16 reads as max.
   * Sent on GET /v1/boxes, GET /v1/boxes/{boxId} and GET /v1/forge/boxes; null when no entry can
   * drop or the version predates it, absent on an older server.
   */
  volatility: z
    .number()
    .nonnegative()
    .nullable()
    .optional()
    .describe(
      "How swingy the current version is: one over the chance of rolling an item worth at least the box's expected value (a box where one roll in ten beats its EV reads 10; every item worth the same reads 1). Read it on a 0-16 scale in four even levels: low [0, 4), medium [4, 8), high [8, 12), max [12, 16]. The number is sent unclamped (a one-in-10,000 chase sends 10000), and anything above 16 reads as max. Sent on GET /v1/boxes, GET /v1/boxes/{boxId} and GET /v1/forge/boxes; null when no entry can drop.",
    ),
  /**
   * The box's own tags as stored on the box (`boxes.tags`), which a tenant copying a box template
   * takes as the copy's tags. Unlike `categories`, nothing derives them from the items. Always
   * sent; `[]` when the box has none. Optional so a client built against an older server still
   * parses its boxes.
   */
  tags: z
    .array(z.string())
    .optional()
    .describe(
      "The box's own tags as stored on the box (for example SNEAKERS, HAS_LIMITED_QTY), which a tenant copying a box template takes as the copy's tags. Unlike categories, nothing derives them from the items. Always sent; [] when the box has none.",
    ),
});
export type BoxSummary = z.infer<typeof boxSummarySchema>;

export const boxDetailSchema = boxSummarySchema.extend({
  ev: z.number().describe(CENTS),
  dropTable: z.array(dropTableItemSchema),
  /**
   * sha256 of this version's entries: the commitment an open settles against. Recompute it from
   * `canonicalEntries` on GET /v1/opens/{openId}/verification; docs/fairness.md gives the form.
   * Stock is excluded, so the value moves only when a new version is published.
   */
  dropTableHash: z
    .string()
    .describe(
      "sha256 of this version's entries: the commitment an open settles against. Recompute it from canonicalEntries on GET /v1/opens/{openId}/verification; docs/fairness.md gives the form.",
    ),
  /** When this version was written; `boxes.refreshed_at` holds the same instant. */
  refreshedAt: z.string().describe("When this version was written."),
  /**
   * Set on a forge box (a box a shopper built): who forged it and the cut this version pays them.
   * null on every other box; optional so a client built against an older server still parses.
   */
  forge: forgeBoxInfoSchema.nullable().optional(),
  /**
   * The opener style this box opens with, resolved box → shop → environment → platform. Optional
   * so a client built against an older server still parses, and one that lacks it opens with its
   * default animation.
   */
  opener: boxOpenerSchema.optional(),
});
export type BoxDetail = z.infer<typeof boxDetailSchema>;

// ---- end users -------------------------------------------------------------------

/** boxed's `KycStatus` lowercased: Veriff's session statuses and lifecycle events. */
export const kycStatusSchema = z.enum([
  "created",
  "started",
  "submitted",
  "approved",
  "declined",
  "resubmission_requested",
  "expired",
  "abandoned",
  "review",
]);
export type KycStatus = z.infer<typeof kycStatusSchema>;

export const endUserSchema = z.object({
  id: z.string(),
  externalId: z.string(),
  /** The user's public name, chosen by them (PUT /v1/me/username); null until they do. */
  username: z.string().nullable(),
  mode: modeSchema,
  balanceCents: z.number().describe(CENTS),
  /**
   * Cents held by an unclaimed box reveal: its price, moved into the hold at the open and released
   * by the claim. A battle seat holds nothing (its entry is debited when it is taken). Spendable =
   * balanceCents − heldCents; show that, not balanceCents.
   */
  heldCents: z.number().describe(CENTS),
  nonce: z.number(),
  /** Reveal mode: the server claims each reveal immediately after it commits. */
  autoClaim: z.boolean(),
  /** The shopper's sound mute, saved on their account. */
  soundMuted: z.boolean(),
  /** The shopper's sound volume, 0 to 100 (50 until they set it). */
  soundVolume: z.int().min(0).max(100),
  /** Provably-fair per-user salt (public: needed to verify past rolls). */
  salt: z.string(),
  /** Locked accounts are refused opens, claims, and balance purchases. */
  locked: z.boolean(),
  /**
   * Identity-verification status (Veriff's own values, lowercased); null when the user never
   * started. Only `kycVerified` says whether they may withdraw where the environment requires it.
   */
  kycStatus: kycStatusSchema.nullable(),
  /** An approved verification that also passed the platform's age and document-country policy. */
  kycVerified: z.boolean(),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});
export type EndUser = z.infer<typeof endUserSchema>;

/** POST /v1/me/kyc/session and /v1/users/{externalId}/kyc/session. */
export const kycSessionRequestSchema = z.object({
  /** Where the hosted verification flow sends the user afterwards (your account page). */
  callbackUrl: z.url().max(2048),
});
export type KycSessionRequest = z.infer<typeof kycSessionRequestSchema>;

export const kycSessionResponseSchema = z.object({
  /** The hosted flow to open in a new tab; the same URL while a session is in flight. */
  url: z.string(),
  status: kycStatusSchema,
});
export type KycSessionResponse = z.infer<typeof kycSessionResponseSchema>;

/**
 * Facts about the shopper's browser a tenant's server may pass along for fraud screening: a
 * signal for SEON, never authorization. `deviceSession` is the SEON JS agent's opaque session.
 */
export const clientContextSchema = z.object({
  ip: z.string().max(64).optional(),
  userAgent: z.string().max(512).optional(),
  deviceSession: z.string().max(8192).optional(),
  /** The shopper's email, when the tenant knows it; not stored, only screened. */
  email: z.email().max(320).optional(),
});
export type ClientContext = z.infer<typeof clientContextSchema>;

export const createUserRequestSchema = z.object({
  externalId: z.string().min(1).max(256),
  metadata: z.record(z.string(), z.unknown()).optional(),
  /** Optional: with the environment's fraud screening on, a new user is screened at creation. */
  client: clientContextSchema.optional(),
});
export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

export const updateMeSettingsRequestSchema = z
  .object({
    autoClaim: z.boolean().optional(),
    soundMuted: z.boolean().optional(),
    soundVolume: z.int().min(0).max(100).optional(),
  })
  .refine(
    (v) => v.autoClaim !== undefined || v.soundMuted !== undefined || v.soundVolume !== undefined,
    { message: "send at least one setting" },
  );
export type UpdateMeSettingsRequest = z.infer<typeof updateMeSettingsRequestSchema>;

// ---- favorites ---------------------------------------------------------------------

/** A box the end user saved to their favorites (the storefront's heart). */
export const boxFavoriteSchema = z.object({
  boxId: z.string(),
  createdAt: z.string().describe("When the box was saved."),
});
export type BoxFavorite = z.infer<typeof boxFavoriteSchema>;

export const boxFavoriteListResponseSchema = z.object({ data: z.array(boxFavoriteSchema) });
export type BoxFavoriteListResponse = z.infer<typeof boxFavoriteListResponseSchema>;

export const boxFavoriteResponseSchema = z.object({ favorite: boxFavoriteSchema });
export type BoxFavoriteResponse = z.infer<typeof boxFavoriteResponseSchema>;

// ---- sessions ----------------------------------------------------------------------

export const createSessionRequestSchema = z.object({
  ttlSeconds: z.int().min(300).max(86_400).optional(),
});
export const sessionResponseSchema = z.object({
  token: z.string(),
  expiresAt: z.string(),
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

// ---- balance --------------------------------------------------------------------

export const creditRequestSchema = z.object({
  amountCents: z.int().positive().describe(CENTS),
  ref: z.string().max(256).optional(),
});
export const adjustmentRequestSchema = z.object({
  amountCents: z
    .int()
    .refine((n) => n !== 0, { message: "amountCents must be non-zero" })
    .describe(CENTS),
  ref: z.string().max(256).optional(),
});
export const ledgerEntrySchema = z.object({
  id: z.string(),
  amountCents: z.number().describe(CENTS),
  balanceAfterCents: z.number().describe(CENTS),
  type: z.enum([
    "credit",
    "claim_debit",
    "battle_entry",
    "adjustment",
    "shipping_fee",
    "shipping_fee_refund",
    "sell_credit",
    "forge_commission",
    "store_credit_out",
    "gift_code",
  ]),
  ref: z.string().nullable(),
  createdAt: z.string(),
});
export type LedgerEntry = z.infer<typeof ledgerEntrySchema>;

/**
 * POST /v1/me/gift-codes/redeem: the `gift_code` ledger row the redemption wrote (its
 * `balanceAfterCents` is the new balance) and the code as the store prints it.
 */
export const redeemGiftCodeResponseSchema = z.object({
  ledger: ledgerEntrySchema,
  code: z.string().describe("The redeemed code, formatted XXXX-XXXX-XXXX-XXXX."),
  amountCents: z.int().describe(CENTS),
});
export type RedeemGiftCodeResponse = z.infer<typeof redeemGiftCodeResponseSchema>;

/** GET /v1/me/balance — the balance in cents plus the environment's display currency. */
export const balanceResponseSchema = z.object({
  balanceCents: z.number().describe(CENTS),
  /**
   * Cents held by an unclaimed box reveal: its price, moved into the hold at the open and released
   * by the claim. A battle seat holds nothing (its entry is debited when it is taken). Spendable =
   * balanceCents − heldCents; show that, not balanceCents.
   */
  heldCents: z.number().describe(CENTS),
  currency: currencySchema,
});
export type BalanceResponse = z.infer<typeof balanceResponseSchema>;

// ---- opens ---------------------------------------------------------------------------

export const openRequestSchema = z.object({
  clientSeed: clientSeedSchema,
  amount: z.int().min(1).max(MAX_OPEN_AMOUNT).optional(),
  expectedVersion: z.int().positive().optional(),
});
export type OpenRequest = z.infer<typeof openRequestSchema>;

export const openEntrySchema = z.object({
  openId: z.string(),
  nonce: z.number(),
  result: z.number(),
  itemIndex: z.number(),
  item: dropTableItemSchema,
});
export type OpenEntry = z.infer<typeof openEntrySchema>;

export const fairnessBundleSchema = z.object({
  seedDate: z.string(),
  serverSeedHash: z.string(),
  clientSeed: z.string(),
  salt: z.string(),
  nonces: z.array(z.number()),
});
export type FairnessBundle = z.infer<typeof fairnessBundleSchema>;

// ---- reveal mode (claims) --------------------------------------------------------
// An open reveals items without debiting the balance; the debit happens at claim, as a
// purchase of the already-revealed items at the price disclosed by the open.

export const claimRequestSchema = z.object({
  /** Guard against claiming at a price the client didn't display. */
  expectedPurchaseAmount: z.int().nonnegative().describe(CENTS).optional(),
});
export type ClaimRequest = z.infer<typeof claimRequestSchema>;

export const claimedItemSchema = z.object({
  openId: z.string(),
  inventoryItemId: z.string(),
  item: dropTableItemSchema,
  isLimited: z
    .boolean()
    .describe(
      "The drop-table entry's isLimited as of the open: a counted item, which is what makes a storefront offer selling it back beside withdrawing it. False for an item won before the field existed.",
    ),
});
export type ClaimedItem = z.infer<typeof claimedItemSchema>;

export const claimResponseSchema = z.object({
  openGroupId: z.string(),
  costCents: z.number().describe(CENTS),
  balanceAfterCents: z.number().describe(CENTS),
  claimedAt: z.string(),
  items: z.array(claimedItemSchema),
});
export type ClaimResponse = z.infer<typeof claimResponseSchema>;

/**
 * POST /v1/reveals/{openGroupId}/sell-back — the claim above followed, in the same transaction, by
 * selling every claimed item back to the house for its value: the ledger shows a `claim_debit`
 * then a `sell_credit`, and `balanceAfterCents` is the balance after both.
 */
export const sellBackResponseSchema = claimResponseSchema.extend({
  /** Σ item.value credited by the sale. */
  soldCents: z.number().describe(CENTS),
  soldAt: z.string(),
});
export type SellBackResponse = z.infer<typeof sellBackResponseSchema>;

// ---- inventory ------------------------------------------------------------------------
// A unit an end user owns after a claim. It leaves the inventory two ways: sold back to the
// house for its value (here), or withdrawn by the tenant's server as a shipped order.

export const INVENTORY_ITEM_STATUSES = [
  "owned",
  "fulfillment_requested",
  "withdrawn",
  "sold",
  "refunded",
] as const;
export type InventoryItemStatus = (typeof INVENTORY_ITEM_STATUSES)[number];

/**
 * One owned unit. `item` is the drop-table entry as it was rolled, so `item.value` is what a
 * sell-back credits however the drop table has been edited since. A unit is sellable when it is
 * `owned` and worth something; the two facts are on the row, so no third field states it.
 */
export const inventoryItemSchema = z.object({
  id: z.string(),
  item: dropTableItemSchema,
  status: z.enum(INVENTORY_ITEM_STATUSES),
  /** Exactly one is set: the open that rolled the unit, the battle open, the seat a loser prize was minted for, or the unit a cancelled order refunded. */
  openId: z.string().nullable(),
  battleOpenId: z.string().nullable(),
  battlePlayerId: z.string().nullable(),
  refundedInventoryItemId: z.string().nullable(),
  createdAt: z.string(),
  /** When a sell-back credited this unit's value; null unless `status` is `sold`. */
  soldAt: z.string().nullable(),
  withdrawalIssue: z
    .enum(INVENTORY_WITHDRAWAL_ISSUES)
    .nullable()
    .describe(
      "Why withdrawing this unit would be refused right now, or null when nothing refuses it: the withdrawal would succeed, or one is already in progress (`status` is `fulfillment_requested`). Worked out from the live catalog item or vendor listing on every read and stored nowhere, so a restock clears it. Advisory only: the order re-checks under a row lock and that check decides.",
    ),
});
export type InventoryItem = z.infer<typeof inventoryItemSchema>;

export const inventoryListResponseSchema = z.object({
  data: z.array(inventoryItemSchema),
  nextCursor: z.string().nullable(),
});
export type InventoryListResponse = z.infer<typeof inventoryListResponseSchema>;

/** How many units one sell-back may name: the sale is one transaction and one ledger row. */
export const SELL_MAX_ITEMS = 100;

export const sellInventoryRequestSchema = z.object({
  inventoryItemIds: z.array(z.string().min(1)).min(1).max(SELL_MAX_ITEMS),
});
export type SellInventoryRequest = z.infer<typeof sellInventoryRequestSchema>;

/**
 * POST /v1/me/inventory/sell — the units named are sold to the house for Σ `item.value` in one
 * transaction, which writes one `sell_credit` ledger row. All or nothing: one unsellable unit
 * refuses the whole sale and names it. The two per-unit routes,
 * POST /v1/me/inventory/{inventoryItemId}/sell and its secret-key twin under
 * /v1/users/{externalId}, answer with this same shape over a single unit.
 */
export const sellInventoryResponseSchema = z.object({
  soldCents: z.number().describe(CENTS),
  balanceAfterCents: z.number().describe(CENTS),
  soldAt: z.string(),
  items: z.array(z.object({ inventoryItemId: z.string(), item: dropTableItemSchema })),
});
export type SellInventoryResponse = z.infer<typeof sellInventoryResponseSchema>;

export const openResponseSchema = z.object({
  openGroupId: z.string(),
  boxId: z.string(),
  boxVersion: z.number(),
  /** Total purchase price of the revealed items. */
  costCents: z.number().describe(CENTS),
  status: z.enum(["unclaimed", "claimed"]),
  /** Set iff the server auto-claimed (status "claimed"). */
  claim: claimResponseSchema.nullable(),
  entries: z.array(openEntrySchema),
  fairness: fairnessBundleSchema,
  /** sha256 of the entries of version `boxVersion`, the table these rolls walked. */
  dropTableHash: z
    .string()
    .describe("sha256 of the entries of version boxVersion: the table these rolls walked."),
});
export type OpenResponse = z.infer<typeof openResponseSchema>;

export const unclaimedRevealSchema = z.object({
  openGroupId: z.string(),
  boxId: z.string(),
  boxVersion: z.number(),
  costCents: z.number().describe(CENTS),
  /** What selling the reveal back instead of claiming it credits: Σ entries[].item.value. */
  sellBackCents: z.number().describe(CENTS),
  createdAt: z.string(),
  entries: z.array(openEntrySchema),
});
export type UnclaimedReveal = z.infer<typeof unclaimedRevealSchema>;

export const unclaimedRevealResponseSchema = z.object({
  /** At most one — a user may hold at most one unclaimed reveal. */
  reveal: unclaimedRevealSchema.nullable(),
});
export type UnclaimedRevealResponse = z.infer<typeof unclaimedRevealResponseSchema>;

export const openRecordSchema = z.object({
  id: z.string(),
  openGroupId: z.string(),
  boxId: z.string(),
  clientSeed: z.string(),
  nonce: z.number(),
  result: z.number(),
  itemIndex: z.number(),
  item: dropTableItemSchema,
  costCents: z.number().describe(CENTS),
  seedDate: z.string(),
  createdAt: z.string(),
});
export type OpenRecord = z.infer<typeof openRecordSchema>;

// ---- fairness -------------------------------------------------------------------------
// seedRevealSchema lives in ./primitives.ts (battles verify against the same daily seed).

/**
 * One drop-table row in the exact form `dropTableHash` is the sha256 of: snake_case database
 * column names, the keys in this declaration order, the array in `position` order. Anyone can
 * therefore recompute the commitment with
 * `sha256(JSON.stringify(canonicalEntries)) === dropTableHash`.
 *
 * The key order is the contract, so the declaration order here must equal the order
 * `canonicalDropTableEntries` builds in `@loot-os/core` (packages/core/src/boxProcessing/hash.ts)
 * and must not be sorted or reordered. `limited_quantity` is absent on purpose: stock moves
 * without a new version, so it is outside what an open commits to.
 */
export const canonicalDropTableEntrySchema = z.object({
  position: z.int().nonnegative(),
  item_source: z.string(),
  catalog_item_id: z.string().nullable(),
  vendor_listing_id: z.string().nullable(),
  external_item_id: z.string().nullable(),
  published_source: z
    .string()
    .nullable()
    .describe(
      "EXTERNAL rows only: the source the entry was published with, which the drop table reports as its itemSource. Null on every other row.",
    ),
  weight: z.int().nonnegative(),
  value_cents: z.int().describe(CENTS),
  value_override_cents: z.int().nullable().describe(CENTS),
  price_cents: z.int().nullable().describe(CENTS),
  is_limited: z.boolean(),
  is_gem_substitute: z.boolean(),
  name: z.string(),
  image_url: z.string(),
});
export type CanonicalDropTableEntry = z.infer<typeof canonicalDropTableEntrySchema>;

export const openVerificationSchema = z.object({
  open: openRecordSchema,
  seed: seedRevealSchema,
  salt: z.string(),
  boxVersion: z.number(),
  dropTable: z.array(dropTableItemSchema),
  /** The version's commitment, written when the version was published. */
  dropTableHash: z
    .string()
    .describe("The version's commitment, written when the version was published."),
  canonicalEntries: z
    .array(canonicalDropTableEntrySchema)
    .describe(
      "The same entries in the form the sha256 reads: sha256(JSON.stringify(canonicalEntries)) equals dropTableHash. Use the array as sent; rebuilding these objects reorders their keys and changes the bytes.",
    ),
});
export type OpenVerification = z.infer<typeof openVerificationSchema>;

/** The longest provably-fair salt: 32 hex characters since 2026-10, 8 (boxed's) before. */
export const SALT_MAX_LENGTH = 32;

export const verifyQuerySchema = z.object({
  serverSeed: z.string().length(64),
  clientSeed: clientSeedSchema,
  salt: z.string().max(SALT_MAX_LENGTH),
  nonce: z.coerce.number().int().nonnegative(),
});
export const verifyResponseSchema = z.object({
  resultString: z.string(),
  resultNumber: z.number(),
});

// ---- hosted storefront ------------------------------------------------------------
// Contract between the platform and the shared multi-tenant storefront: a tenant's
// config (theme, branding, features, domains) drives how its storefront renders.

/**
 * The design tokens a storefront can override live in ./storefrontTokens.ts,
 * re-exported above — the theme-var resolver needs them without importing this
 * module back.
 */

/**
 * Starting points a storefront can be populated from. Each carries the full
 * token set, so applying one is just a bulk fill of the same fields a tenant
 * could type by hand — nothing about a preset is remembered after it is applied.
 *
 * All three share the stock palette's lightness ladder and differ only in hue
 * and chroma, so contrast holds up identically whichever one a tenant picks.
 */
export const STOREFRONT_THEME_PRESETS = [
  {
    id: "midnight",
    name: "Midnight",
    description: "The house palette — violet on deep navy.",
    tokens: STOREFRONT_TOKEN_DEFAULTS,
  },
  {
    id: "ember",
    name: "Ember",
    description: "Warm and high-energy — amber on near-black brown.",
    tokens: {
      background: "#140b06",
      foreground: "#eee6e2",
      border: "#372419",
      input: "#372419",
      ring: "#f3821d",
      card: "#1f130c",
      "card-foreground": "#eee6e2",
      popover: "#1f130c",
      "popover-foreground": "#eee6e2",
      muted: "#342117",
      "muted-foreground": "#a28d82",
      primary: "#f3821d",
      "primary-foreground": "#140b06",
      secondary: "#342117",
      "secondary-foreground": "#eee6e2",
      accent: "#342117",
      "accent-foreground": "#eee6e2",
      destructive: "#fb2c36",
      "destructive-foreground": "#f6f9fc",
      "chart-1": "#f3821d",
      "chart-2": "#e3b916",
      "chart-3": "#f5886c",
      "chart-4": "#88cc1c",
      "chart-5": "#f4a1a0",
      sidebar: "#1b100a",
      "sidebar-foreground": "#eee6e2",
      "sidebar-primary": "#f3821d",
      "sidebar-primary-foreground": "#140b06",
      "sidebar-accent": "#342117",
      "sidebar-accent-foreground": "#eee6e2",
      "sidebar-border": "#372419",
      "sidebar-ring": "#f3821d",
    },
  },
  {
    id: "aurora",
    name: "Aurora",
    description: "Cool and clean — cyan on deep teal.",
    tokens: {
      background: "#050f14",
      foreground: "#e1e9ed",
      border: "#152c37",
      input: "#152c37",
      ring: "#00c9c9",
      card: "#09181f",
      "card-foreground": "#e1e9ed",
      popover: "#09181f",
      "popover-foreground": "#e1e9ed",
      muted: "#132a34",
      "muted-foreground": "#8096a1",
      primary: "#00c9c9",
      "primary-foreground": "#050f14",
      secondary: "#132a34",
      "secondary-foreground": "#e1e9ed",
      accent: "#132a34",
      "accent-foreground": "#e1e9ed",
      destructive: "#fb2c36",
      "destructive-foreground": "#f6f9fc",
      "chart-1": "#00c9c9",
      "chart-2": "#43dd9a",
      "chart-3": "#60b0ff",
      "chart-4": "#88cc1c",
      "chart-5": "#c4acf2",
      sidebar: "#08151b",
      "sidebar-foreground": "#e1e9ed",
      "sidebar-primary": "#00c9c9",
      "sidebar-primary-foreground": "#050f14",
      "sidebar-accent": "#132a34",
      "sidebar-accent-foreground": "#e1e9ed",
      "sidebar-border": "#152c37",
      "sidebar-ring": "#00c9c9",
    },
  },
] as const satisfies readonly {
  id: string;
  name: string;
  description: string;
  tokens: Record<StorefrontTokenKey, string>;
}[];

export type StorefrontThemePresetId = (typeof STOREFRONT_THEME_PRESETS)[number]["id"];

/**
 * The rarity tiers a storefront can recolor live in ./rarityTiers.ts, re-exported
 * above — resolveRarityColor needs them without importing this module back.
 */

/**
 * Storefront theme, stored in storefront_configs.theme and resolved per domain.
 * `tokens` addresses shadcn variables one for one; anything unset falls through
 * to STOREFRONT_TOKEN_DEFAULTS. The embeddable opener's LootTheme is derived
 * from these same tokens rather than stored separately.
 */
export const lootThemeSchema = z.object({
  tokens: z.partialRecord(z.enum(STOREFRONT_TOKEN_KEYS), z.string()).optional(),
  /**
   * Per-rarity override colors keyed "0".."5" (hex or "r,g,b" triplets).
   * Partial like `tokens`: a tier left out falls through to the opener's rail
   * palette, which is what every consumer of this field already assumes.
   */
  rarity: z.partialRecord(z.enum(STOREFRONT_RARITY_KEYS), z.string()).optional(),
  radius: z.string().optional(),
  /**
   * The body font stack, set as `font-family` on the page. A stack whose first family is in
   * `STOREFRONT_FONTS` gets that font's files loaded (`toLoadableFontStack`).
   */
  fontFamily: z.string().optional(),
  /**
   * The font stack for headings (h1–h3, as `--ds-font-heading`), loaded the same way. Unset =
   * headings in the body font.
   */
  headingFontFamily: z.string().optional(),
  /** The design system's groups: semantic colors, type, shape, surface, motion, stage, layout. */
  ...themeSpecShape,
});
export type StorefrontTheme = z.infer<typeof lootThemeSchema>;

/**
 * A theme as `storefront.update` takes it: the stored shape, refusing a token or rarity color the
 * resolver would drop and naming its key. Writers only: the stored theme and the resolved config
 * are read through `lootThemeSchema`, so a theme saved before this check still parses.
 */
export const lootThemeInputSchema = lootThemeSchema.superRefine(refuseUnacceptedThemeColors);

/**
 * A Shopify shop's opener theme (`shopify_shops.opener_theme`, `ShopifyOpenerTheme`): opener theme
 * overrides in the same shape an opener style's `theme` has. Written whole by both editors (the
 * dashboard's Shopify page and the embedded admin); `resolveShopifyOpenerTheme` sanitizes it again
 * on read, because the value lands on the merchant's own storefront.
 */
export const shopifyOpenerThemeSchema = openerThemeOverridesSchema;

/**
 * The storefront sections a tenant switches per environment, each from the dashboard page that
 * owns it (Payments, Battles, Chat, Forge). The account, inventory and fairness pages are not here: every
 * storefront serves them, so they have no switch.
 */
export const STOREFRONT_FEATURE_KEYS = ["balancePurchases", "battles", "chat", "forge"] as const;
export type StorefrontFeatureKey = (typeof STOREFRONT_FEATURE_KEYS)[number];

export const STOREFRONT_FEATURE_DEFAULTS: Record<StorefrontFeatureKey, boolean> = {
  balancePurchases: false,
  /**
   * Off → the storefront hides the battle routes and the API refuses create and join with
   * `battles_disabled`. Battles already created keep revealing, completing and claiming: the
   * money is committed.
   */
  battles: false,
  /**
   * Off → the storefront hides the chat panel and `/v1/chat` refuses reads and writes with
   * `chat_disabled`. Staff moderation in the dashboard keeps working.
   */
  chat: false,
  /**
   * Off → the storefront hides the forge routes and the API refuses every shopper forge route
   * (claiming earnings included) with `forge_disabled`. Opening a forge box that already exists
   * is an ordinary open, gated by `opens` alone, and it keeps accruing its creator's commission.
   */
  forge: false,
};

/** Merges stored overrides onto defaults so new keys never need a backfill. */
export function resolveStorefrontFeatures(
  overrides?: Partial<Record<StorefrontFeatureKey, boolean>> | null,
): Record<StorefrontFeatureKey, boolean> {
  return { ...STOREFRONT_FEATURE_DEFAULTS, ...(overrides ?? {}) };
}

export const storefrontFeaturesSchema = z
  .partialRecord(z.enum(STOREFRONT_FEATURE_KEYS), z.boolean())
  .default({});

export const storefrontBrandingSchema = z.object({
  logoUrl: z.string(),
  faviconUrl: z.string(),
  badgeUrl: z.string(),
});

/**
 * Intercom live chat, resolved for the SPA. `appId` is "" when chat is off — the tenant's
 * per-environment switch is off, or the stage has no Intercom workspace — so it is the one
 * thing the SPA checks before loading anything from Intercom.
 */
export const storefrontIntercomSchema = z.object({
  appId: z.string(),
  /**
   * Tenant-uploaded launcher icon as SVG source, "" for the default glyph. Same rules as
   * `currency.iconSvg`: embed via `svgIconDataUri` in an `<img>`, never inline.
   */
  launcherIconSvg: z.string(),
});
export type StorefrontIntercom = z.infer<typeof storefrontIntercomSchema>;

/**
 * Termly cookie consent, resolved for the SPA. `websiteUuid` is the Termly website whose banner
 * this storefront shows and whose consent log records the shopper's choices: the environment's
 * own, or the platform default. "" when the stage has no Termly website, so it is the one thing
 * the SPA checks before loading anything from Termly.
 */
export const storefrontTermlySchema = z.object({
  websiteUuid: z.string(),
  /**
   * The banner's theme, resolved: the environment's own or the platform default, `{}` while
   * neither is set. The SPA injects it as `--cookie-banner-*` variables beside the page theme; a
   * token left out follows the store's own palette.
   */
  theme: cookieBannerThemeSchema,
});
export type StorefrontTermly = z.infer<typeof storefrontTermlySchema>;

/**
 * A Termly website UUID as staff paste it from Termly's embed snippet (the id in
 * `app.termly.io/resource-blocker/{uuid}`). Public, not a credential: it is in every page's
 * script URL.
 */
export const termlyWebsiteUuidSchema = z.uuid();

/** GET /v1/storefront/config — resolved by hostname; consumed by the storefront BFF. */
export const storefrontConfigSchema = z.object({
  tenantId: z.string(),
  /** The environment this storefront serves (live or a sandbox). */
  environmentId: z.string(),
  mode: modeSchema,
  slug: z.string(),
  name: z.string(),
  primaryDomain: z.string(),
  customDomains: z.array(z.string()),
  /**
   * The domain the storefront's emails send from (`no-reply@` it): `mail.` in front of the primary
   * domain, once that is a custom domain whose DKIM records SES has verified. null = the platform
   * address.
   */
  emailDomain: z.string().nullable(),
  theme: lootThemeSchema,
  branding: storefrontBrandingSchema,
  features: z.record(z.enum(STOREFRONT_FEATURE_KEYS), z.boolean()),
  /** BFF-only: injected as x-publishable-key on proxied /v1 calls, never sent to browsers. */
  publishableKey: z.string(),
  /** How this environment shows cents; credits by default. */
  currency: currencySchema,
  /** Platform staff turned SEON screening on for this environment: the SPA loads the SEON agent. */
  fraudScreening: z.boolean(),
  /** Identity verification is required here (on by default): the account page offers the Veriff flow. */
  kycRequired: z.boolean(),
  /** Live chat: the SPA mounts its Intercom launcher only when `intercom.appId` is non-empty. */
  intercom: storefrontIntercomSchema,
  /** Cookie consent: the SPA loads Termly's banner only when `termly.websiteUuid` is non-empty. */
  termly: storefrontTermlySchema,
  /** Whether the SPA records browser analytics events, and under what consent (`analytics.ts`). */
  analytics: storefrontAnalyticsSchema,
  /** The stage's AppSync Events endpoints for battles; both "" = none, so the SPA polls. */
  realtime: storefrontRealtimeSchema,
  /**
   * Where this environment ships: the enabled destinations of its shipping config (its own
   * override, or the platform default), in the configured order. `[]` = shipping is unavailable
   * here, and the account page says so instead of offering an address form.
   */
  shipping: storefrontShippingSchema,
  /**
   * Who the Terms of Service and Privacy Policy name: the environment's own legal identity on
   * `storefront_configs.legal`, or the platform default while that is NULL. `legalEmail` is
   * already the support address when none is set.
   */
  legal: publicStorefrontLegalSchema,
  /**
   * The tenant's social profiles, shown as icons in the footer: each set network's `https` URL,
   * in `storefront_configs.social_links`. `{}` = no icons.
   */
  socialLinks: socialLinksSchema,
  /**
   * The box categories the boxes page offers, in list order (`storefront_configs.box_categories`,
   * every one until an editor saves a choice). `[]` = no category row. A box names its own on
   * `BoxSummary.categories`, so the page shows only the ones a listed box is in.
   */
  boxCategories: z.array(boxCategorySchema),
  /**
   * The storefront's words for its nouns (box, item, inventory, battle), every term resolved: the
   * tenant's override from `storefront_configs.terminology`, else the default.
   */
  terminology: storefrontTerminologySchema,
  /** The storefront template every page renders (`storefront_configs.storefront_template`). */
  template: storefrontTemplateIdSchema,
  /**
   * The store's own layout per page over `template` (`storefront_configs.page_templates`), each a
   * layout the page offers. `{}` = every page from the template.
   */
  pageTemplates: pageTemplateChoicesSchema,
  /**
   * The store's sounds for the box opener and battles, resolved over the platform defaults
   * (`storefront_configs.sounds` over `platform_settings.sounds`). `{}` = every event is silent;
   * there are no built-in sounds.
   */
  sounds: storefrontSoundsSchema,
  /** The forge builder's commission settings (`features.forge` says whether it is shown at all). */
  forge: storefrontForgeSchema,
  /** The notifications module is on: the SPA shows the bell to a signed-in shopper. */
  notifications: z.object({ enabled: z.boolean() }),
  updatedAt: z.string(),
});
export type StorefrontConfig = z.infer<typeof storefrontConfigSchema>;

export const storefrontConfigResponseSchema = z.object({ config: storefrontConfigSchema });

// Shopper auth (internal-secret-gated; tenant resolved from `host`).

export const storefrontShopperSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  email: z.string(),
});
export type StorefrontShopper = z.infer<typeof storefrontShopperSchema>;

export const storefrontMagicLinkRequestSchema = z.object({
  host: z.string().min(1).max(255),
  email: z.email().max(320),
  redirectPath: z.string().max(512).optional(),
  clientIp: z.string().max(64).optional(),
  userAgent: z.string().max(512).optional(),
  /** SEON JS-agent session from the login page; stored on the token and screened at verify. */
  deviceSession: z.string().max(8192).optional(),
  /** sha256 (base64url) of the requesting browser's nonce cookie, added to the link as `n`. */
  nonceHash: z
    .string()
    .regex(/^[A-Za-z0-9_-]{43}$/)
    .optional(),
});
export type StorefrontMagicLinkRequest = z.infer<typeof storefrontMagicLinkRequestSchema>;

export const storefrontVerifyRequestSchema = z.object({
  host: z.string().min(1).max(255),
  token: z.string().min(1).max(256),
});
export const storefrontShopperSessionResponseSchema = z.object({
  sessionToken: z.string(),
  expiresAt: z.string(),
  shopper: storefrontShopperSchema,
});
export type StorefrontShopperSessionResponse = z.infer<
  typeof storefrontShopperSessionResponseSchema
>;

export const storefrontSessionRequestSchema = z.object({
  host: z.string().min(1).max(255),
  sessionToken: z.string().min(1).max(256),
});

/**
 * What a signed-in shopper boots the Intercom Messenger with. `userId` is the shopper id and
 * `userHash` its Identity Verification HMAC, both minted by the platform from the session.
 */
export const storefrontIntercomIdentitySchema = z.object({
  userId: z.string(),
  userHash: z.string(),
});
export type StorefrontIntercomIdentity = z.infer<typeof storefrontIntercomIdentitySchema>;

export const storefrontMeResponseSchema = z.object({
  shopper: storefrontShopperSchema,
  /** null when live chat is off for this storefront or the stage has no Intercom secrets. */
  intercom: storefrontIntercomIdentitySchema.nullable(),
});
export type StorefrontMeResponse = z.infer<typeof storefrontMeResponseSchema>;

export const storefrontLogoutRequestSchema = z.object({
  sessionToken: z.string().min(1).max(256),
});

// ---- balance packs + purchases (Stripe checkout, requires the balancePurchases feature) ----

export const balancePackSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** What the shopper pays, in USD cents (real money). */
  priceCents: z.int().describe("Charged amount in USD cents."),
  /** What lands in the balance; more than priceCents for a bonus pack. */
  creditedCents: z.int().describe(CENTS),
  sortOrder: z.int(),
});
export type BalancePack = z.infer<typeof balancePackSchema>;

export const storefrontBalancePacksResponseSchema = z.object({
  packs: z.array(balancePackSchema),
});

export const storefrontCheckoutRequestSchema = z.object({
  host: z.string().min(1).max(255),
  sessionToken: z.string().min(1).max(256),
  packId: z.string().min(1).max(64),
  clientIp: z.string().max(64).optional(),
  userAgent: z.string().max(512).optional(),
  deviceSession: z.string().max(8192).optional(),
});

export const storefrontCheckoutResponseSchema = z.object({
  /** Stripe-hosted Checkout URL to redirect the shopper to. */
  url: z.string(),
});

export const BALANCE_PURCHASE_STATUS_VALUES = [
  "pending",
  "completed",
  "failed",
  "refunded",
  "disputed",
] as const;

export const storefrontPurchaseStatusRequestSchema = z.object({
  host: z.string().min(1).max(255),
  sessionToken: z.string().min(1).max(256),
  checkoutSessionId: z.string().min(1).max(255),
});

export const storefrontPurchaseStatusResponseSchema = z.object({
  status: z.enum(BALANCE_PURCHASE_STATUS_VALUES),
  creditedCents: z.int().describe(CENTS),
  /** Shopper's balance once the purchase has completed; null while pending. */
  balanceCents: z.int().nullable().describe(CENTS),
});

// ---- pagination -----------------------------------------------------------------------

export const paginationQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

// ---- boxes: list filter ------------------------------------------------------------------------

/**
 * GET /v1/boxes. `type` left out lists every box but forge boxes (`COMMUNITY`), which is what the
 * route listed before the forge existed; `COMMUNITY` lists the forge boxes alone.
 */
export const boxListQuerySchema = z.object({
  type: z.enum(["NORMAL", "VENDING_MACHINE", "COMMUNITY"]).optional(),
});

// ---- forge: responses that reuse the box and ledger shapes -------------------------------------

/** A forge box in the browse grid: a box summary plus who forged it. */
export const forgeBoxSummarySchema = boxSummarySchema.extend({ forge: forgeBoxInfoSchema });
export type ForgeBoxSummary = z.infer<typeof forgeBoxSummarySchema>;

export const forgeBoxListResponseSchema = z.object({
  data: z.array(forgeBoxSummarySchema),
  nextCursor: z.string().nullable(),
});
export type ForgeBoxListResponse = z.infer<typeof forgeBoxListResponseSchema>;

/** One of the caller's own forge boxes, as the "my forge" page lists it. */
export const myForgeBoxSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  imageUrl: z.string(),
  status: z.enum(["draft", "pre_release", "active", "archived"]),
  priceCents: z.int().describe(CENTS),
  version: z.int(),
  /** The cut in force, in percent: what the next version is priced and paid at. */
  creatorCutPercent: z.number(),
  openedCount: z.int(),
  /**
   * What purchases of this box have earned its creator, claimed or not, floored to cents per box.
   * Sub-cent shares add up across boxes, so these need not sum to what a claim pays.
   */
  lifetimeEarningsCents: z.int().describe(CENTS),
  createdAt: z.string(),
  /** How swingy the current version is, as on `GET /v1/boxes`; null when no entry can drop. */
  volatility: z.number().nonnegative().nullable(),
  /** The current version's most valuable item that can drop; absent when none can. */
  topItem: boxTopItemSchema.optional(),
});
export type MyForgeBox = z.infer<typeof myForgeBoxSchema>;

/**
 * The creator's all-time figures over every forge box they built, deleted ones too: what their
 * boxes earned them, what shoppers paid to open them (direct opens once claimed, and each battle
 * round a shopper's seat paid for), and how often they were opened.
 */
export const myForgeLifetimeSchema = z.object({
  /** Their commission, claimed or not, floored to cents. */
  earnedCents: z.int().describe(CENTS),
  /** What shoppers spent opening their boxes. */
  spentCents: z.int().describe(CENTS),
  opens: z.int(),
});
export type MyForgeLifetime = z.infer<typeof myForgeLifetimeSchema>;

/** GET /v1/me/forge: every forge box the caller has not deleted, newest first, and their unclaimed earnings. */
export const myForgeResponseSchema = z.object({
  boxes: z.array(myForgeBoxSchema),
  /** What `POST /v1/me/forge/earnings/claim` would pay now, floored to cents. */
  earningsCents: z.int().describe(CENTS),
  /** All-time figures over every box they built, deleted ones included. */
  lifetime: myForgeLifetimeSchema,
});
export type MyForgeResponse = z.infer<typeof myForgeResponseSchema>;

/**
 * POST /v1/forge/boxes and PUT …/drop-table: the owner's box as it now stands (`box`), and the
 * published version a shopper opens (`detail`, the `GET /v1/boxes/{boxId}` shape).
 */
export const forgeBoxWriteResponseSchema = z.object({
  box: myForgeBoxSchema,
  detail: boxDetailSchema,
});
export type ForgeBoxWriteResponse = z.infer<typeof forgeBoxWriteResponseSchema>;

/** PATCH /v1/forge/boxes/{boxId}. */
export const forgeBoxStatusResponseSchema = z.object({ box: myForgeBoxSchema });
export type ForgeBoxStatusResponse = z.infer<typeof forgeBoxStatusResponseSchema>;

/** POST /v1/me/forge/earnings/claim: the `forge_commission` ledger row the claim wrote. */
export const forgeClaimResponseSchema = z.object({
  ledger: ledgerEntrySchema,
  amountCents: z.int().describe(CENTS),
  replayed: z.boolean(),
});
export type ForgeClaimResponse = z.infer<typeof forgeClaimResponseSchema>;
